import axios from 'axios';

// Lambda endpoint that returns S3 presigned POST data for the
// `media/public/generative-ai/` prefix on the staging bucket.
const GENERATIVE_AI_PRESIGN_URL =
  'https://axv5oderowddyswclf3bqpmkla0hwfup.lambda-url.us-east-1.on.aws/';

// In-memory cache of previously uploaded files keyed by content hash.
// Lives only for the current page session — module state resets on page
// refresh / tab close, which matches the requirement that the cache must
// not persist across refreshes or new sessions.
const uploadedUrlCache = new Map();

// SHA-256 hex digest of a File/Blob — same algorithm used elsewhere in the
// app (see calculateFileHash in generativeAITrigger.js) so cache keys stay
// consistent with the rest of the AI pipeline.
const computeFileHash = async (blob) => {
  const buffer = await blob.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

export const getCachedGenerativeAIUpload = (hash) =>
  hash ? uploadedUrlCache.get(hash) : undefined;

export const cacheGenerativeAIUpload = (hash, url) => {
  if (hash && url) uploadedUrlCache.set(hash, url);
};

export const clearGenerativeAIUploadCache = () => {
  uploadedUrlCache.clear();
};

const sanitizeObjectName = (name) =>
  (name || `edited-image-${Date.now()}.jpg`)
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_');

const buildUniqueObjectName = (name) => {
  const safe = sanitizeObjectName(name);
  const dot = safe.lastIndexOf('.');
  const base = dot > 0 ? safe.slice(0, dot) : safe;
  const ext = dot > 0 ? safe.slice(dot) : '.jpg';
  const unique = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  return `${base}_${unique}${ext}`;
};

const getPresignedPost = async (objectName) => {
  const response = await axios.post(GENERATIVE_AI_PRESIGN_URL, null, {
    params: { object_name: objectName },
  });
  return response?.data;
};

const postFileToS3 = async (file, presigned) => {
  const formData = new FormData();
  Object.entries(presigned.fields || {}).forEach(([k, v]) => {
    formData.append(k, v);
  });
  formData.append('file', file);

  return axios.post(presigned.url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Uploads a File/Blob to the generative-ai S3 prefix via the Lambda-signed POST
// flow and returns the final public URL.
//
// Session caching: callers can pass `cacheKey` (typically a SHA-256 image hash)
// to reuse a prior upload within the same page session. When omitted, the file
// is hashed in-place. Pass `skipCache: true` to bypass cache entirely.
export async function uploadGenerativeAIFile(file, suggestedName, options = {}) {
  if (!file) throw new Error('uploadGenerativeAIFile: file is required');

  const { skipCache = false } = options;
  let cacheKey = options.cacheKey;

  if (!skipCache && !cacheKey) {
    try {
      cacheKey = await computeFileHash(file);
    } catch (err) {
      // Hash failures should not block the upload — just skip caching.
      console.warn('uploadGenerativeAIFile: hash failed, skipping cache', err);
    }
  }

  if (!skipCache && cacheKey) {
    const cached = uploadedUrlCache.get(cacheKey);
    if (cached) return cached;
  }

  const objectName = buildUniqueObjectName(suggestedName || file.name);
  const presigned = await getPresignedPost(objectName);

  if (!presigned?.url || !presigned?.fields?.key) {
    throw new Error('uploadGenerativeAIFile: invalid presigned response');
  }

  await postFileToS3(file, presigned);
  const base = presigned.url;
  // Transform S3 URL to CDN URL format
  const cdnUrl = base?.replace(
    /^https:\/\/lf-v2-media-staging\.s3\.amazonaws\.com\//,
    'https://media-stag.imagine.io/'
  );
  const finalUrl = `${cdnUrl}${presigned.fields.key}`;

  if (!skipCache && cacheKey) uploadedUrlCache.set(cacheKey, finalUrl);

  return finalUrl;
}

// Exposed for callers that want to compute the hash themselves (e.g. to also
// short-circuit before generating the canvas image at all).
export const computeGenerativeAIFileHash = computeFileHash;
